import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import NewsletterCapture from "./NewsletterCapture";
import PropertyPulse from "./PropertyPulse";

/**
 * Footer — proper site footer with newsletter, nav, contact, and legal.
 * Sits above PropertyPulse (which serves as a market-pulse strip),
 * followed by copyright.
 */
const NAV = {
  Services: [
    ["Window cleaning", "/services"],
    ["Pressure washing", "/services"],
    ["Awning cleaning", "/services"],
    ["Outdoor lighting", "/services"],
    ["Full portfolio walk", "/quote"],
  ],
  Company: [
    ["About", "/about"],
    ["Why Bespoke", "/why-bespoke"],
    ["Process", "/process"],
    ["Gallery", "/gallery"],
    ["Learning Center", "/learning"],
  ],
  Resources: [
    ["AI Property Audit", "/audit"],
    ["À la carte pricing", "/one-off"],
    ["Referral program", "/referral"],
    ["DI water · WFP", "/di-water"],
    ["Industries served", "/industries"],
  ],
};

export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <>
      {/* Property manager pulse ticker */}
      <PropertyPulse />

      {/* Main footer */}
      <footer
        className="relative"
        style={{
          background: "linear-gradient(180deg, #050506 0%, #020204 100%)",
          borderTop: "1px solid var(--line)",
          color: "var(--text-2)",
        }}
        data-testid="site-footer"
      >
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14 py-20 md:py-24">
          {/* Top row — brand + newsletter */}
          <div className="grid lg:grid-cols-12 gap-14 mb-16">
            <div className="lg:col-span-5">
              <div className="font-display" style={{ fontSize: "2.4rem", letterSpacing: "-0.03em", lineHeight: 1, color: "var(--text)" }}>
                Bespoke
              </div>
              <div className="font-display-italic mt-1" style={{ fontSize: "1.4rem", color: "var(--gold-2)" }}>
                Exterior Solutions
              </div>
              <p className="mt-6 font-body max-w-md" style={{ fontSize: 15, lineHeight: 1.65 }}>
                Premium exterior maintenance for commercial portfolios across NYC and Charlotte NC. Disabled veteran-owned. $2M insured. Photo debrief every visit.
              </p>
              <div className="mt-8 space-y-2.5 font-mono text-[11px]" style={{ color: "var(--text-2)" }}>
                <div className="flex items-center gap-3">
                  <Phone size={13} style={{ color: "var(--gold)" }} />
                  <a href="tel:+18338237765" className="hover:text-white transition-colors" data-testid="footer-phone">
                    (833) 8BESPOKE
                  </a>
                  <span style={{ color: "var(--text-3)" }}>·</span>
                  <a href="tel:+15182472467" className="hover:text-white transition-colors">
                    (518) 247-2467
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Mail size={13} style={{ color: "var(--gold)" }} />
                  <a href="mailto:hello@bespokeexteriorsolutions.com" className="hover:text-white transition-colors" data-testid="footer-email">
                    hello@bespokeexteriorsolutions.com
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin size={13} style={{ color: "var(--gold)" }} />
                  <a
                    href="https://maps.google.com/?q=185+Great+Neck+Rd+4th+Floor+Great+Neck+NY+11021"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white transition-colors"
                    data-testid="footer-address"
                  >
                    185 Great Neck Rd, 4th Fl · Great Neck, NY 11021
                  </a>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <NewsletterCapture variant="footer" source="footer" />
            </div>
          </div>

          {/* Nav columns */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-10 pb-14" style={{ borderBottom: "1px solid var(--line)" }}>
            {Object.entries(NAV).map(([col, items]) => (
              <div key={col}>
                <div className="font-mono text-[10px] uppercase tracking-[0.42em] mb-5" style={{ color: "var(--gold-2)" }}>
                  {col}
                </div>
                <ul className="space-y-3">
                  {items.map(([label, href]) => (
                    <li key={label}>
                      <Link
                        to={href}
                        className="font-body text-sm transition-colors hover:text-white"
                        style={{ color: "var(--text-2)" }}
                        data-testid={`footer-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Bottom bar */}
          <div className="pt-8 flex flex-wrap items-center justify-between gap-4 font-mono text-[10px] uppercase tracking-[0.34em]" style={{ color: "var(--text-3)" }}>
            <div>
              © {year} Bespoke Exterior Solutions LLC · All rights reserved
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/referral" className="hover:text-white transition-colors">Referral</Link>
              <a href="#" className="hover:text-white transition-colors" data-testid="footer-unsubscribe">Unsubscribe</a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
